import { makeObservable, observable, reaction, action, computed, runInAction } from 'mobx'
import dayjs from 'dayjs'
import { type IAvatarProps } from 'shared/ui'
import { Base } from 'models'
import type { ICallItem, ICallItemType, ICallPeopleStatus, IUpdatedCallItem } from 'entities/Call'

export class CallPeople extends Base {
  id: number
  name: string
  desc: string
  avatarProps: IAvatarProps
  type: ICallItemType
  status: ICallPeopleStatus = 'default'
  isHold = false
  isHoldDisabled = false
  isHoldLoading = false
  isDeleteLoading = false
  timestamp = 0
  startedAt: number | null = null
  is_participant = false
  timeInterval: ReturnType<typeof setInterval> | null = null
  disabled = false
  national_number?: string

  constructor(item: ICallItem) {
    super()

    this.id = item.id
    this.name = item.name
    this.desc = item.desc || ''
    this.avatarProps = item.avatarProps
    this.type = item.type
    this.disabled = item.disabled || false
    this.is_participant = item.is_participant || false
    this.national_number = item.national_number || ''

    makeObservable(this, {
      name: observable,
      desc: observable,
      national_number: observable,
      avatarProps: observable,
      status: observable,
      timestamp: observable,
      is_participant: observable,
      label: computed,
      handleIncrementTime: action,
      resetIncrementTime: action,
      syncOrigin: action,
      isHold: observable,
      isHoldDisabled: observable,
      isHoldLoading: observable,
      isDeleteLoading: observable,
    })

    this.reactionStatus()
  }

  get uniqueId() {
    if (this.type === 'inbox') {
      return `inbox_id:${this.id}`
    }

    if (this.type === 'contact') {
      return `contact_id:${this.id}`
    }

    return `number:+1${this.id}`
  }

  get label() {
    if (this.isHold) {
      return 'Hold'
    }

    if (this.status === 'calling') {
      return 'Calling...'
    }

    if (this.status === 'connected') {
      return dayjs(this.timestamp).format('mm:ss')
    }

    return this.desc
  }

  updateIsParticipant = (status: boolean) => {
    this.is_participant = status
  }

  updateStatus = (status: ICallPeopleStatus) => {
    this.status = status
  }

  updateIsDeleteLoading = (status: boolean) => {
    this.isDeleteLoading = status
  }

  updateIsHold = (status: boolean) => {
    this.isHold = status
  }

  updateIsHoldDisabled = (status: boolean) => {
    this.isHoldDisabled = status
  }

  updateIsHoldLoading = (status: boolean) => {
    this.isHoldLoading = status
  }

  setStartedAt = (value: number | null) => {
    this.startedAt = value
  }

  handleIncrementTime = () => {
    if (this.timeInterval) {
      clearInterval(this.timeInterval)
      this.timeInterval = null
    }

    if (this.startedAt) {
      this.timestamp = Date.now() - this.startedAt
    }

    this.timeInterval = setInterval(() => {
      runInAction(() => {
        this.timestamp = this.startedAt ? Date.now() - this.startedAt : this.timestamp + 1000
      })
    }, 1000)
  }

  resetIncrementTime = () => {
    runInAction(() => {
      this.timestamp = 0
    })

    if (this.timeInterval) {
      clearInterval(this.timeInterval)
      this.timeInterval = null
    }
  }

  reactionStatus = () => {
    reaction(
      () => this.status,
      (value) => {
        if (value === 'connected') {
          this.handleIncrementTime()
        } else {
          this.resetIncrementTime()
        }
      }
    )
  }

  syncOrigin = (updatedItem: IUpdatedCallItem) => {
    this.name = updatedItem.name
    this.desc = updatedItem.desc
    this.national_number = updatedItem.national_number
    this.avatarProps = {
      ...this.avatarProps,
      ...updatedItem.avatarProps,
    }
  }
}
